import { useEffect, useMemo, useRef, useState } from 'react';
import { BUILD_INFO } from './build-info';

export interface RuntimeSnapshot {
  buildSha: string;
  builtAt: string;
  innerWidth: number;
  innerHeight: number;
  clientWidth: number;
  clientHeight: number;
  visualViewportWidth: number | null;
  visualViewportHeight: number | null;
  visualViewportScale: number | null;
  devicePixelRatio: number;
  rootFontSize: number;
  safeAreaTop: string;
  safeAreaRight: string;
  safeAreaBottom: string;
  safeAreaLeft: string;
  tgViewportHeight: number | null;
  tgStableHeight: number | null;
  tgPlatform: string | null;
  tgVersion: string | null;
  tgExpanded: boolean | null;
  userAgent: string;
  breakpointMode: 'narrow' | 'wide';
}

function readRuntimeSnapshot(): RuntimeSnapshot {
  const doc = document.documentElement;
  const bodyStyles = window.getComputedStyle(doc);
  const viewport = window.visualViewport;
  const tg = window.Telegram?.WebApp;
  const clientWidth = doc.clientWidth;

  return {
    buildSha: BUILD_INFO.sha,
    builtAt: BUILD_INFO.builtAt,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    clientWidth,
    clientHeight: doc.clientHeight,
    visualViewportWidth: viewport?.width ?? null,
    visualViewportHeight: viewport?.height ?? null,
    visualViewportScale: viewport?.scale ?? null,
    devicePixelRatio: window.devicePixelRatio,
    rootFontSize: Number.parseFloat(bodyStyles.fontSize),
    safeAreaTop: bodyStyles.getPropertyValue('--safe-area-top').trim(),
    safeAreaRight: bodyStyles.getPropertyValue('--safe-area-right').trim(),
    safeAreaBottom: bodyStyles.getPropertyValue('--safe-area-bottom').trim(),
    safeAreaLeft: bodyStyles.getPropertyValue('--safe-area-left').trim(),
    tgViewportHeight: tg?.viewportHeight ?? null,
    tgStableHeight: tg?.viewportStableHeight ?? null,
    tgPlatform: tg?.platform ?? null,
    tgVersion: tg?.version ?? null,
    tgExpanded: tg?.isExpanded ?? null,
    userAgent: navigator.userAgent,
    breakpointMode: clientWidth >= 430 ? 'wide' : 'narrow',
  };
}

function applyRuntimeCssVars(snapshot: RuntimeSnapshot) {
  const doc = document.documentElement;
  const visualViewportHeight = snapshot.visualViewportHeight ?? snapshot.innerHeight;
  const effectiveHeight = snapshot.tgStableHeight ?? snapshot.tgViewportHeight ?? visualViewportHeight;

  doc.style.setProperty('--app-height', `${Math.round(effectiveHeight)}px`);
  doc.style.setProperty('--app-width', `${snapshot.clientWidth}px`);
  doc.style.setProperty('--tg-viewport-height', `${Math.round(snapshot.tgViewportHeight ?? visualViewportHeight)}px`);
  doc.style.setProperty('--tg-stable-height', `${Math.round(snapshot.tgStableHeight ?? effectiveHeight)}px`);
  doc.style.setProperty('--runtime-breakpoint', snapshot.breakpointMode);
  doc.dataset.runtimeBreakpoint = snapshot.breakpointMode;
  doc.dataset.runtimeBuild = snapshot.buildSha;
}

function isDebugEnabled() {
  const params = new URLSearchParams(window.location.search);
  return params.get('runtimeDebug') === '1' || window.localStorage.getItem('miniapp:runtimeDebug') === '1';
}

export function useRuntimeDiagnostics() {
  const [snapshot, setSnapshot] = useState<RuntimeSnapshot>(() => readRuntimeSnapshot());
  const loggedRef = useRef(false);

  useEffect(() => {
    const update = () => {
      const nextSnapshot = readRuntimeSnapshot();
      applyRuntimeCssVars(nextSnapshot);
      setSnapshot(nextSnapshot);
    };

    update();

    const viewport = window.visualViewport;
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
    };
  }, []);

  useEffect(() => {
    if (loggedRef.current) return;
    loggedRef.current = true;
    // This log gives us a deterministic runtime fingerprint inside Telegram WebView.
    // eslint-disable-next-line no-console
    console.info('[miniapp-runtime:init]', snapshot);
  }, [snapshot]);

  const debugEnabled = useMemo(() => isDebugEnabled(), []);

  return { snapshot, debugEnabled };
}
